import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import stylesDefault from '../default.module.css';
import styles from './heroBannerManager.module.css';
import { Icons } from '../../../../code/IconHandler';
import { showErrorToast, showInfoToast } from '../../../../code/ToastHelper';
import { usePermissionGuard } from '../../adminPanelTools';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

export default function HeroBannerManager() {
    const checking = usePermissionGuard('handle_herobanner');
    const EditIcon = Icons.Edit;
    const DeleteIcon = Icons.Delete;
    const [localChanges, setLocalChanges] = useState({});
    const fileInputRef = useRef(null);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        const loadCategories = async () => {
            try {
                const res = await axios.get('/api/herobanner/all/categories');
                const fetchedCategories = res.data;
                setCategories(fetchedCategories);
            } catch (err) {
                console.error('❌ Failed to load hero banner categories:', err);
            }
        };

        loadCategories();
    }, []);

    const [currentCategory, setCurrentCategory] = useState('startseite');
    const [images, setImages] = useState([]);
    const [alt, setAlt] = useState('');
    const [imagePos, setImagePos] = useState('center');
    const [imageFile, setImageFile] = useState(null);

    useEffect(() => {
        if (currentCategory) fetchImages();
    }, [currentCategory]);

    const fetchImages = async () => {
        try {
            const res = await axios.get(`/api/herobanner/${currentCategory}`);
            setImages(res.data);
        } catch (err) {
            console.error('❌ Error loading images', err);
        }
    };

    const handleDragEnd = async (result) => {
        if (!result.destination) return;

        const reordered = [...images];
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);

        // Update position field
        const updated = reordered.map((img, index) => ({
            ...img,
            position: index,
        }));

        setImages(updated);

        // Save new positions
        for (let img of updated) {
            await updateImage(img.id, img.alt, img.position, img.image_pos);
        }
    };

    const uploadImage = async (e) => {
        e.preventDefault();
        if (!imageFile) return;

        // Calculate nextPosition freshly
        const positions = images.map(img => img.position);
        const nextPosition = positions.length > 0 ? Math.max(...positions) + 1 : 0;
        const formData = new FormData();
        formData.append('image', imageFile);
        formData.append('alt', alt);
        formData.append('position', nextPosition); // ✅ Add this
        formData.append('image_pos', imagePos);

        try {
            await axios.post(`/api/herobanner/${currentCategory}`, formData);
            setAlt('');
            setImageFile(null);
            fileInputRef.current.value = '';
            setImagePos('center');
            fetchImages();
            showInfoToast(`Bild ${imageFile.name} erfolgreich hochgeladen!`);
        } catch (err) {
            console.error('❌ Upload failed:', err);
            showErrorToast('Fehler beim Hochladen.');
        }
    };

    const deleteImage = async (img) => {
        if (!window.confirm('Bild wirklich löschen?')) return;
        try {
            await axios.delete(`/api/herobanner/${img.id}`);
            fetchImages();
            const fileName = img.path.split(/[/\\]/).pop(); // Handles both \ and / as separators
            showInfoToast(`Bild ${fileName} erfolgreich gelöscht!`);
        } catch (err) {
            console.error('❌ Delete failed', err);
            showErrorToast('Löschen fehlgeschlagen!');
        }
    };

    const updateImage = async (id, alt, position, image_pos = 'center') => {
        try {
            await axios.put(`/api/herobanner/${id}`, { alt, position, image_pos });
            fetchImages();
        } catch (err) {
            console.error('❌ Update failed', err);
        }
    };

    if (checking) return <div className={stylesDefault.container}>Lade...</div>;

    return (
        <div className={stylesDefault.container}>
            <h1>Hero-Banner verwalten</h1>
            <div className={styles.formGroup}>
                <label className={styles.label}>Kategorie wählen:</label>
                <select
                    value={currentCategory}
                    onChange={(e) => setCurrentCategory(e.target.value)}
                    className={styles.selectStyled}
                >
                    {categories.map(c => (
                        <option key={c.slug} value={c.slug}>{c.name}</option>
                    ))}
                </select>
            </div>
            <form onSubmit={uploadImage} className={styles.uploadForm}>


                <div className={styles.formGroup}>
                    <label className={styles.label}>Bild auswählen:</label>
                    <input
                        type="file"
                        accept="image/*"
                        required
                        onChange={e => setImageFile(e.target.files[0])}
                        className={styles.fileInput}
                        ref={fileInputRef}
                    />
                </div>

                <div className={styles.formGroup}>
                    <label className={styles.label}
                        title="Dieser Text wird blinden Nutzer:innen vorgelesen (Screenreader)."
                    >Text (Barrierefreiheit):</label>
                    <input
                        type="text"
                        placeholder="Alt-Text eingeben"
                        value={alt}
                        required
                        onChange={e => setAlt(e.target.value)}
                        className={styles.textInput}
                    />
                </div>
                <div className={styles.formGroup}>
                    <label className={styles.label}
                        title="Bei zu großen Bildern, wird etwas abgeschnitten. Welcher Bereich soll fokussiert werden?"
                    >Bildposition:</label>
                    <select
                        value={imagePos}
                        onChange={(e) => setImagePos(e.target.value)}
                        className={styles.selectStyled}
                    >
                        <option value="top">Oben</option>
                        <option value="center">Mitte</option>
                        <option value="bottom">Unten</option>
                    </select>
                </div>

                <button className={stylesDefault.greenButton} type="submit">Bild hochladen</button>
            </form>

            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="heroGrid" direction="horizontal">
                    {(provided) => (
                        <div
                            className={styles.imageList}
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                        >
                            {images.length === 0 ? (
                                <div className={styles.noImages}>Bislang keine Bilder vorhanden</div>
                            ) : (images
                                .sort((a, b) => a.position - b.position)
                                .map((img, index) => (
                                    <Draggable key={img.id} draggableId={img.id.toString()} index={index}>
                                        {(provided) => (
                                            <div
                                                className={styles.imageRow}
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                            >
                                                <div className={styles.positionBadge}>{img.position}</div>
                                                <img src={img.path} alt={img.alt} className={styles.bannerImageLarge}
                                                    style={{ objectPosition: `center ${img.image_pos || 'center'}` }}
                                                />
                                                <div className={styles.imageMeta}>
                                                    <input
                                                        type="text"
                                                        value={img.alt}
                                                        onChange={(e) => {
                                                            const newAlt = e.target.value;
                                                            setImages(prev =>
                                                                prev.map(i => i.id === img.id ? { ...i, alt: newAlt } : i)
                                                            );

                                                            const originalAlt = images.find(i => i.id === img.id)?.alt || '';
                                                            setLocalChanges(prev => ({
                                                                ...prev,
                                                                [img.id]: newAlt !== originalAlt
                                                            }));
                                                        }}
                                                    />
                                                    <select
                                                        value={img.image_pos}
                                                        onChange={(e) => {
                                                            const newPos = e.target.value;
                                                            setImages(prev =>
                                                                prev.map(i => i.id === img.id ? { ...i, image_pos: newPos } : i)
                                                            );

                                                            const originalPos = images.find(i => i.id === img.id)?.image_pos || 'center';
                                                            setLocalChanges(prev => ({
                                                                ...prev,
                                                                [img.id]: newPos !== originalPos || prev[img.id],
                                                            }));
                                                        }}
                                                        className={styles.selectStyled}
                                                    >
                                                        <option value="top">Oben</option>
                                                        <option value="center">Mitte</option>
                                                        <option value="bottom">Unten</option>
                                                    </select>
                                                    <div className={styles.imageActions}>
                                                        <button
                                                            className={stylesDefault.blueButton}
                                                            onClick={() => {
                                                                updateImage(img.id, img.alt, img.position, img.image_pos);
                                                                setLocalChanges(prev => {
                                                                    const copy = { ...prev };
                                                                    delete copy[img.id];
                                                                    return copy;
                                                                });
                                                            }}
                                                            disabled={!localChanges[img.id]}
                                                        >
                                                            <EditIcon />
                                                        </button>
                                                        <button
                                                            className={stylesDefault.redButton}
                                                            onClick={() => deleteImage(img)}
                                                        >
                                                            <DeleteIcon />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))
                            )}


                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
}
